import { useState, useEffect } from 'react';
import { Box, Stack, Typography, IconButton, alpha, Collapse } from '@mui/material';
import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded';
import ErrorOutlineRounded from '@mui/icons-material/ErrorOutlineRounded';
import InfoOutlinedRounded from '@mui/icons-material/InfoOutlined';
import CloseRounded        from '@mui/icons-material/CloseRounded';
import { useListOpenIssuesQuery, type IssueSeverity, type PlatformIssue } from '../store/api/platformApi';

const DISMISS_KEY = 'imperium_dismissed_platform_issues';

const SEVERITY_META: Record<IssueSeverity, { color: string; bg: string; Icon: typeof WarningAmberRounded; alwaysShow: boolean }> = {
  info:     { color: '#60A5FA', bg: 'rgba(96, 165, 250, 0.10)',  Icon: InfoOutlinedRounded,  alwaysShow: false },
  low:      { color: '#34D399', bg: 'rgba(52, 211, 153, 0.10)',  Icon: InfoOutlinedRounded,  alwaysShow: false },
  medium:   { color: '#FBBF24', bg: 'rgba(251, 191, 36, 0.10)',  Icon: WarningAmberRounded,  alwaysShow: false },
  high:     { color: '#F59E0B', bg: 'rgba(245, 158, 11, 0.12)',  Icon: WarningAmberRounded,  alwaysShow: true  },
  critical: { color: '#EF4444', bg: 'rgba(239, 68, 68, 0.14)',   Icon: ErrorOutlineRounded,  alwaysShow: true  },
};

/**
 * Reglas para mostrar el banner top:
 *  - severidad high o critical → siempre.
 *  - cualquier severidad si el comunicado tiene notifyInApp=true (es un comunicado importante).
 *  - resto se queda solo en Gustavo / campana.
 */
function shouldShowBanner(issue: PlatformIssue): boolean {
  return SEVERITY_META[issue.severity]?.alwaysShow || !!issue.notifyInApp;
}

function readDismissed(): Set<string> {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return new Set();
    return new Set(JSON.parse(raw) as string[]);
  } catch { return new Set(); }
}

function writeDismissed(ids: Set<string>) {
  try { localStorage.setItem(DISMISS_KEY, JSON.stringify([...ids])); } catch { /* ignore */ }
}

/**
 * Banner top-of-app que muestra incidencias publicadas con severidad alta o
 * crítica. Se ve en TODAS las páginas autenticadas. Cada usuario puede
 * descartar uno (persistido en localStorage por id de issue).
 */
export function PlatformIssueBanner() {
  const { data: issues = [] } = useListOpenIssuesQuery(undefined, {
    pollingInterval: 5 * 60_000,  // refrescar cada 5 min
  });

  const [dismissed, setDismissed] = useState<Set<string>>(() => readDismissed());

  // Si la lista de issues cambia (alguno se cerró), limpiar dismisses huérfanos
  useEffect(() => {
    const ids = new Set(issues.map((i) => i._id));
    let changed = false;
    const next = new Set(dismissed);
    for (const id of dismissed) {
      if (!ids.has(id)) { next.delete(id); changed = true; }
    }
    if (changed) { setDismissed(next); writeDismissed(next); }
  }, [issues, dismissed]);

  const visible = issues.find((i) => shouldShowBanner(i) && !dismissed.has(i._id));
  if (!visible) return null;

  const meta = SEVERITY_META[visible.severity];
  const Icon = meta.Icon;

  const handleDismiss = () => {
    const next = new Set(dismissed);
    next.add(visible._id);
    setDismissed(next);
    writeDismissed(next);
  };

  return (
    <Collapse in>
      <Box
        sx={{
          bgcolor: meta.bg,
          borderBottom: `1px solid ${alpha(meta.color, 0.4)}`,
          color: meta.color,
        }}
      >
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{ maxWidth: 1280, mx: 'auto', px: { xs: 2, sm: 3 }, py: 1.25 }}
        >
          <Icon sx={{ fontSize: 20, flexShrink: 0 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 700, color: meta.color, lineHeight: 1.3 }}>
              {visible.title}
            </Typography>
            {visible.description && (
              <Typography
                variant="caption"
                sx={{ color: 'text.primary', display: 'block', mt: 0.25, opacity: 0.85, lineHeight: 1.4 }}
              >
                {visible.description}
              </Typography>
            )}
          </Box>
          <IconButton
            size="small"
            onClick={handleDismiss}
            sx={{ color: meta.color, flexShrink: 0, '&:hover': { bgcolor: alpha(meta.color, 0.1) } }}
            aria-label="Descartar"
          >
            <CloseRounded sx={{ fontSize: 18 }} />
          </IconButton>
        </Stack>
      </Box>
    </Collapse>
  );
}

// Re-export para conveniencia
export type { PlatformIssue };
